import { useEffect, useState } from 'react';
import { CompanyConfigResourceApi, IndexTerminology } from '@root/api';
import { log, logAttributes } from '@root/app/shared/new-services/logging.service';

/**
 * Index terminology feature flag (per company): HAPPINESS (default, historical wording) or
 * WELLBEING. The dashboard reads it to decide, for each affected i18n key, whether to show the
 * "Happiness/HI" wording or its "Wellbeing/WI" sibling variant (see {@link useTranslation}).
 *
 * The value is fetched once from the current company config and cached at module level, mirroring
 * the pattern in `hf-ratamode-message/hfRataMode.tsx`. A synchronous getter is exposed so the
 * (synchronous) translation wrapper can consult the flag without awaiting.
 */

// module-level cache to store the resolved terminology value
let terminologyCache: IndexTerminology | null = null;
let terminologyPromise: Promise<IndexTerminology> | null = null;

/**
 * Fetches the company's index terminology once and caches it. Falls back to HAPPINESS on error or
 * when the backend does not resolve a value, so existing companies keep the historical wording.
 */
export const fetchIndexTerminologyOnce = (): Promise<IndexTerminology> => {
    if (terminologyCache !== null) {
        return Promise.resolve(terminologyCache);
    }

    if (terminologyPromise) {
        return terminologyPromise;
    }

    terminologyPromise = new CompanyConfigResourceApi()
        .getCompanyCurrentCompanyConfig()
        .then((response) => {
            terminologyCache = response.data.indexTerminologyOrDefault
                ?? response.data.indexTerminology
                ?? IndexTerminology.Happiness;
            return terminologyCache;
        })
        .catch((error) => {
            // Example: a degraded-but-recovered path is `warn`, not `error` — nothing is
            // broken for the user, they just get the default wording.
            //
            // This replaces a `console.error(error)` that dumped the whole serialised
            // AxiosError into the log stream. Logging the two facts that matter — that we
            // fell back, and to what — keeps the event searchable instead of a wall of text,
            // and it won't be mistaken for a real failure. The underlying request is already
            // reported by the Axios interceptor, so there's no need to restate it here.
            log.warn('Index terminology fetch failed, using default', logAttributes({
                'app.fallback': IndexTerminology.Happiness,
                'error.type': (error as { code?: string })?.code,
                'http.response.status_code': (error as { response?: { status?: number } })?.response?.status
            }));

            terminologyCache = IndexTerminology.Happiness;
            return IndexTerminology.Happiness;
        })
        .finally(() => {
            terminologyPromise = null;
        });

    return terminologyPromise;
};

/**
 * Synchronous read of the cached terminology. Returns HAPPINESS until the async fetch has resolved
 * (and on any failure), so callers always get a safe default that matches existing companies.
 */
export const getCachedIndexTerminology = (): IndexTerminology =>
    terminologyCache ?? IndexTerminology.Happiness;

/**
 * Clears the cached value so the next read re-fetches. Call this after the flag is changed in
 * settings so the dashboard reflects the new wording without a full reload.
 */
export const invalidateIndexTerminologyCache = (): void => {
    terminologyCache = null;
    terminologyPromise = null;
};

/**
 * Hook variant: triggers the one-time fetch on mount and re-renders the consumer when the value
 * arrives. Use it in components that must react to the flag; the synchronous getter above is enough
 * for the translation wrapper.
 */
export const useIndexTerminology = (): IndexTerminology => {
    const [terminology, setTerminology] = useState<IndexTerminology>(getCachedIndexTerminology());

    useEffect(() => {
        fetchIndexTerminologyOnce().then(setTerminology);
    }, []);

    return terminology;
};
